import React, { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useFormSave } from '../hooks/useFormSave';
import { ApplicationForm } from '../types';
import FileUpload from './FileUpload';
import styles from './ProjectContent.module.css';

import { combineClasses } from '../utils/combineClasses';

// Character limits based on page equivalents
export const LIMITS = {
  executiveSummary: 2000, // ~1 página
};

interface ProjectContentProps {
  readOnly?: boolean;
}

const ProjectContent: React.FC<ProjectContentProps> = ({ readOnly = false }) => {
  // Initialize form save hook for context awareness
  useFormSave({ stepId: 'content' });

  const {
    register,
    formState: { errors },
    watch,
    control,
  } = useFormContext<ApplicationForm>();

  const executiveSummary = watch('executiveSummary') || '';
  const charCount = executiveSummary.length;

  // Persistent validation that doesn't depend on React Hook Form errors
  const persistentValidation = useMemo(() => {
    return {
      isOverLimit: charCount > LIMITS.executiveSummary,
      isNearLimit: charCount > LIMITS.executiveSummary * 0.8 && charCount <= LIMITS.executiveSummary,
      message: `Resumen Ejecutivo no puede exceder ${LIMITS.executiveSummary} caracteres`
    };
  }, [charCount]);

  return (
    <div>
      <h2 className={styles.sectionTitle}>Contenido del Proyecto</h2>

      <div className={styles.sectionInfoBox}>
        <strong>Instrucciones importantes:</strong>
        <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
          <li>
            Complete el resumen ejecutivo respetando el límite de caracteres
          </li>
          <li>
            Suba un documento PDF con el contenido completo del proyecto según
            la plantilla
          </li>
          <li>
            El documento debe contener el mismo resumen ingresado en el
            formulario
          </li>
          <li>Los campos se guardan automáticamente mientras escribe</li>
        </ul>
      </div>

      {/* Executive Summary */}
      <div className={styles.formGroup}>
        <label className={styles.label} htmlFor="executiveSummary">
          Resumen Ejecutivo *
        </label>
        <textarea
          id="executiveSummary"
          className={combineClasses(
            styles.textarea,
            styles['rows-6' as keyof typeof styles],
            errors.executiveSummary && styles.error,
            readOnly && styles.readOnlyField
          )}
          {...register('executiveSummary', {
            required: 'Resumen Ejecutivo es requerido',
            maxLength: {
              value: LIMITS.executiveSummary,
              message: `Resumen Ejecutivo no puede exceder ${LIMITS.executiveSummary} caracteres`,
            },
          })}
          placeholder="Describa de manera concisa los objetivos, metodología y resultados esperados de su proyecto..."
          readOnly={readOnly}
        />
        <div
          className={combineClasses(
            styles.characterCount,
            persistentValidation.isOverLimit && styles.overLimit,
            persistentValidation.isNearLimit && styles.nearLimit
          )}
        >
          {charCount.toLocaleString('es-CL')} /{' '}
          {LIMITS.executiveSummary.toLocaleString('es-CL')} caracteres
        </div>
        {(errors.executiveSummary || persistentValidation.isOverLimit) && (
          <span className={styles.errorMessage}>
            {errors.executiveSummary?.message || persistentValidation.message}
          </span>
        )}
        <small className={styles.helpText}>
          Máximo 1 página (~2000 caracteres). Este resumen debe ser comprensible
          para evaluadores de diferentes disciplinas.
        </small>
      </div>

      {/* Project Document Upload */}
      <div className={styles.formGroup}>
        <label className={styles.label}>Documento del Proyecto *</label>
        <Controller
          name="projectDocument"
          control={control}
          rules={{ required: 'El documento del proyecto es requerido' }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <FileUpload
              sectionType="PROJECT_DESCRIPTION"
              label=""
              description="Suba un documento que contenga toda la información del proyecto incluyendo: hipótesis, metodología, pertinencia nacional, plan de trabajo, transferibilidad global, resultados esperados y carta Gantt."
              options={{
                acceptedTypes: ['.pdf', '.doc', '.docx'],
                maxFiles: 1,
              }}
              value={value ? [value] : []}
              onChange={(fileIds) => onChange(fileIds[0] || '')}
              error={error?.message}
              required
              disabled={readOnly}
              templateFile={{
                url: "/templates/Avanza_UC_Formulacion Proyecto.docx",
                filename: "Avanza_UC_Formulacion Proyecto.docx",
                label: "Descargar plantilla del documento"
              }}
            />
          )}
        />
      </div>
    </div>
  );
};

export default ProjectContent;
